import { useMemo } from "react";
import { DocumentDtoStatusEnum, DocumentDtoTypeEnum, Role } from "../../../api";
import { useMe } from "../../../swr";
import { useDocuments } from "../../settings/hooks/useDocuments";

const REQUIRED_DOCUMENT_TYPES: DocumentDtoTypeEnum[] = ["CR", "TAX_CARD"];

export function useProductRequestSubmissionEligibility() {
  const { user, isLoading: isLoadingUser } = useMe();
  const { data: documentsData, isLoading: isLoadingDocuments } = useDocuments({
    page: 1,
    pageSize: 100,
  });

  const evaluation = useMemo(() => {
    const isAdmin = user?.role === Role.Admin;
    if (isAdmin) {
      return { canSubmit: true, reason: null as string | null };
    }

    const documents = documentsData?.data ?? [];
    const requiredDocuments = documents.filter((doc) =>
      REQUIRED_DOCUMENT_TYPES.includes(doc.type as DocumentDtoTypeEnum),
    );

    const approvedTypes = new Set(
      requiredDocuments
        .filter((doc) => doc.status === DocumentDtoStatusEnum.Approved)
        .map((doc) => doc.type as DocumentDtoTypeEnum),
    );
    const pendingRequired = requiredDocuments.some(
      (doc) => doc.status === DocumentDtoStatusEnum.Pending,
    );
    const rejectedRequired = requiredDocuments.some(
      (doc) => doc.status === DocumentDtoStatusEnum.Rejected,
    );
    const missingRequired = REQUIRED_DOCUMENT_TYPES.filter((type) => !approvedTypes.has(type));

    if (missingRequired.length > 0) {
      return {
        canSubmit: false,
        reason:
          "Your CR and Tax Card must be approved before you can submit a product request.",
      };
    }

    if (pendingRequired) {
      return {
        canSubmit: false,
        reason:
          "Your documents are still under review. You can submit a profile change request, but product requests are locked until approval.",
      };
    }

    if (rejectedRequired) {
      return {
        canSubmit: false,
        reason:
          "One or more required documents were rejected. Please update your profile or re-upload them before creating a product request.",
      };
    }

    return { canSubmit: true, reason: null as string | null };
  }, [documentsData?.data, user?.role]);

  return {
    isLoading: isLoadingUser || isLoadingDocuments,
    canSubmit: evaluation.canSubmit,
    reason: evaluation.reason,
  };
}
